import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AccessGroupsService } from 'users/services/access-groups.service';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { User } from 'users/interfaces/user.interface';
import { AccessGroup } from 'users/interfaces/access-group.interface';
import { EventBusService } from 'shared/services/event-bus.service';
import { ToastService } from 'shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { FormDifferService } from 'shared/services/form-differ.service';
import { SupplierUsersService } from 'users/services/supplier-users.service';
import { TabComponentInterface } from 'shared/interfaces/tab-component.interface';

@Component({
    selector: 'app-edit-user-listing-access',
    templateUrl: './supplier-user-edit-listing-access.component.html',
    styleUrls: ['./supplier-user-edit-listing-access.component.scss'],
    standalone: false,
})
export class SupplierUserEditListingAccessComponent implements OnInit, OnDestroy, TabComponentInterface {
    private subscriptions: { [key: string]: Subscription | undefined } = {};

    public data!: User;
    public supplierAccessGroups!: AccessGroup[];
    public editUserAccessGroupForm = new UntypedFormGroup({
        listingAccessGroupsInput: new UntypedFormControl([]),
    });

    constructor(
        private accessGroupsService: AccessGroupsService,
        private toastService: ToastService,
        private translate: TranslateService,
        private formDifferService: FormDifferService,
        private supplierUserService: SupplierUsersService,
        private eventBusService: EventBusService,
    ) {}

    public ngOnInit(): void {
        this.formDifferService.registerForm(this.editUserAccessGroupForm);

        this.subscriptions.modalMainAction = this.eventBusService.getObservableForEventStream('modalMainButtonAction').subscribe((data) => {
            if (data === 'listingAccess') {
                this.updateUserListingAccess();
            }
        });

        this.subscriptions.getAccessGroupsForSupplier = this.accessGroupsService
            .getAccessGroupBySupplierId(this.data.supplierId)
            .subscribe((accessGroups: AccessGroup[]) => {
                this.supplierAccessGroups = accessGroups;
                this.formDifferService.createDataSnapshot();
            });

        this.editUserAccessGroupForm
            .get('listingAccessGroupsInput')
            ?.setValue(this.data.listingAccessGroups.map((accessGroup) => accessGroup.id));
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public canDeactivateTab(): boolean {
        const canDeactivate = !this.formDifferService.hasDataChanged();

        if (!canDeactivate) {
            this.toastService.showToast('generalKeywords.unsavedTabChanges', 'error', {}, 8000);
        }

        return canDeactivate;
    }

    public updateUserListingAccess() {
        if (Object.keys(this.formDifferService.getDiffWithSnapshot()).length === 0) {
            this.closeModal();
            return;
        }

        this.editUserAccessGroupForm.markAllAsTouched();
        this.editUserAccessGroupForm.get('listingAccessGroupsInput')?.updateValueAndValidity();

        if (this.editUserAccessGroupForm.valid) {
            const body = {
                id: this.data.id,
                listingAccessGroupsInput: this.editUserAccessGroupForm.get('listingAccessGroupsInput')?.value,
            } as User;

            this.subscriptions.updateSupplierUser = this.supplierUserService.updateSupplierUser(body).subscribe(() => {
                this.toastService.showToast('users.supplierUsers.edit.listingAccess.updateSuccess', 'success', {
                    firstName: this.data.firstName,
                    lastName: this.data.lastName,
                });
                this.closeModal(true);
            });
        }
    }

    private closeModal(refreshGrid: boolean = false) {
        this.eventBusService.dispatch('closeUserModal', refreshGrid);
    }
}
